import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Sun, Moon, Menu, Bell, Search, LogOut, X, Shield, Cloud, AlertTriangle,
  Sparkles, Eye, Scale, Activity, ChevronDown,
  Briefcase, GitMerge, Server, LifeBuoy,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useAuth } from '../../lib/auth'
import { api } from '../../lib/api'
import WhatsNewBadge from '../WhatsNewBadge'
import TenantSwitcher from './TenantSwitcher'
import { Link } from 'react-router-dom'
import { Users } from 'lucide-react'

interface TopNavProps {
  theme: 'dark' | 'light'
  onToggleTheme: () => void
  onToggleSidebar: () => void
}

const CLOCKS = [
  { label: 'Sydney', tz: 'Australia/Sydney' },
  { label: 'Tokyo',  tz: 'Asia/Tokyo' },
  { label: 'SG',     tz: 'Asia/Singapore' },
  { label: 'India',  tz: 'Asia/Kolkata' },
  { label: 'Dubai',  tz: 'Asia/Dubai' },
  { label: 'Paris',  tz: 'Europe/Paris' },
  { label: 'London', tz: 'Europe/London' },
  { label: 'NYC',    tz: 'America/New_York' },
  { label: 'SF',     tz: 'America/Los_Angeles' },
]

function WorldClocks() {
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 10_000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="hidden xl:flex items-center gap-3">
      {CLOCKS.map(clock => {
        const time = now.toLocaleTimeString('en-GB', { timeZone: clock.tz, hour: '2-digit', minute: '2-digit', hour12: false })
        const hour = parseInt(time.split(':')[0])
        const isDay = hour >= 7 && hour < 20
        return (
          <div key={clock.label} className="flex items-center gap-1 text-[10px]">
            <span className={isDay ? 'text-amber' : 'text-blue'}>{isDay ? '☀' : '☾'}</span>
            <span className="text-text-muted">{clock.label}</span>
            <span className="font-mono text-text-primary font-medium">{time}</span>
          </div>
        )
      })}
    </div>
  )
}

/** Small chip showing projected 12-month analyst hours avoided by the platform.
 *  Click to navigate to the full Headcount Avoided page. Refreshes every 5 min. */
function SavingsChip() {
  const [annualHours, setAnnualHours] = useState<number | null>(null)
  const [fte, setFte]                 = useState<number | null>(null)
  useEffect(() => {
    const pull = () => {
      api.get('/api/savings/summary')
        .then(({ data }) => {
          setAnnualHours(Math.round(data?.annual_rate_hours ?? 0))
          setFte(data?.fte_equivalent ?? 0)
        })
        .catch(() => {})
    }
    pull()
    const id = setInterval(pull, 300_000)
    return () => clearInterval(id)
  }, [])
  // Hide the chip entirely until we cross a whole FTE — sub-1 FTE isn't a
  // chip-worthy value claim. The full /app/savings page stays accessible
  // via direct URL or the (also-conditional) sidebar entry.
  if (annualHours === null || fte === null || fte < 1) return null
  const display = annualHours >= 1000
    ? (annualHours / 1000).toFixed(1) + 'k h'
    : annualHours.toLocaleString('en-GB') + ' h'
  return (
    <Link to="/app/savings"
      className="hidden md:inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-emerald/30 bg-emerald/5 hover:bg-emerald/10 text-emerald-light text-[10px] font-mono transition-colors"
      title={`Projected 12-month analyst hours avoided · ${fte.toFixed(2)} FTE-equivalent`}>
      <Users size={11} />
      <span className="font-semibold">{display}/yr</span>
      <span className="text-text-muted">· {fte.toFixed(2)} FTE</span>
    </Link>
  )
}

// Quick persona switcher — shortcut into /app/landing/<id> without
// going through the chooser. Reads /api/personas so newly-registered
// personas (e.g. CIO / SDLC / Hosting / Helpdesk) appear automatically
// without a frontend rebuild. Persists last pick to localStorage so the
// chooser auto-redirects on next visit. See PersonaChooserPage.
interface RegistryPersona {
  id: string
  label: string
  icon: string
  route: string
}

const PERSONA_ICON_MAP: Record<string, React.ElementType> = {
  Sparkles, Shield, Scale, Eye, Activity,
  Briefcase, GitMerge, Server, LifeBuoy,
}

// Fallback used until /api/personas resolves (or in offline/dev mode).
// Keeps the dropdown usable even when the backend is unreachable.
const FALLBACK_PERSONAS: RegistryPersona[] = [
  { id: 'cto',        label: 'CTO',        icon: 'Sparkles',  route: '/app/landing/cto' },
  { id: 'cio',        label: 'CIO',        icon: 'Briefcase', route: '/app/landing/cio' },
  { id: 'ciso',       label: 'CISO',       icon: 'Shield',    route: '/app/landing/ciso' },
  { id: 'compliance', label: 'Compliance', icon: 'Scale',     route: '/app/landing/compliance' },
  { id: 'soc',        label: 'SOC',        icon: 'Eye',       route: '/app/landing/soc' },
  { id: 'sdlc',       label: 'SDLC',       icon: 'GitMerge',  route: '/app/landing/sdlc' },
  { id: 'hosting',    label: 'Hosting',    icon: 'Server',    route: '/app/landing/hosting' },
  { id: 'helpdesk',   label: 'Helpdesk',   icon: 'LifeBuoy',  route: '/app/landing/helpdesk' },
  { id: 'devops',     label: 'DevOps',     icon: 'Activity',  route: '/app/landing/devops' },
]

function MyViewSwitcher() {
  const [open, setOpen] = useState(false)
  const [personas, setPersonas] = useState<RegistryPersona[]>(FALLBACK_PERSONAS)
  const navigate = useNavigate()

  useEffect(() => {
    api.get('/api/personas')
      .then(({ data }) => {
        const list = (data?.personas || []) as RegistryPersona[]
        if (Array.isArray(list) && list.length > 0) setPersonas(list)
      })
      .catch(() => {})
  }, [])

  const choose = (id: string) => {
    try { localStorage.setItem('cdm-persona', id) } catch {}
    setOpen(false)
    navigate(`/app/landing/${id}`)
  }
  return (
    <div className="relative hidden md:block">
      <button type="button" onClick={() => setOpen(!open)}
        className="btn-ghost p-1 rounded-lg flex items-center gap-1 text-[11px]"
        title="My View — switch persona landing page">
        <Sparkles size={13} />
        <span className="hidden lg:inline">My View</span>
        <ChevronDown size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-48 bg-surface border border-border rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest text-text-muted">
              Persona
            </div>
            {personas.map(p => {
              const Icon = PERSONA_ICON_MAP[p.icon] || Sparkles
              return (
                <button key={p.id} type="button" onClick={() => choose(p.id)}
                  className="w-full text-left px-3 py-1.5 hover:bg-surface-raised flex items-center gap-2 text-[11px]">
                  <Icon size={12} className="text-blue-light" />
                  <span>{p.label}</span>
                </button>
              )
            })}
            <div className="border-t border-border">
              <Link to="/app/landing" onClick={() => setOpen(false)}
                className="block px-3 py-1.5 hover:bg-surface-raised text-[10px] text-text-muted">
                Open chooser…
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function NotificationBell() {
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<any[]>([])
  const [unread, setUnread] = useState(0)

  useEffect(() => {
    const fetch = () => {
      api.get('/api/notifications/unread-count').then(({ data }) => setUnread(data.unread || 0)).catch(() => {})
    }
    fetch()
    const i = setInterval(fetch, 60_000)
    return () => clearInterval(i)
  }, [])

  const loadNotifications = async () => {
    try {
      const { data } = await api.get('/api/notifications', { params: { limit: 15 } })
      setNotifications(data.notifications || [])
    } catch {}
  }

  const toggle = () => {
    if (!open) loadNotifications()
    setOpen(!open)
  }

  const markAllRead = async () => {
    await api.put('/api/notifications/read-all')
    setUnread(0)
    setNotifications(notifications.map(n => ({ ...n, is_read: true })))
  }

  const SEV_ICON: Record<string, React.ElementType> = { critical: Shield, high: AlertTriangle, cloud: Cloud }

  return (
    <div className="relative">
      <button type="button" onClick={toggle} className="btn-ghost p-1 rounded-lg relative" title="Notifications">
        <Bell size={14} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose text-white text-[8px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-80 bg-surface border border-border rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
              <span className="text-xs font-semibold text-text-primary">Notifications</span>
              <div className="flex gap-2">
                {unread > 0 && (
                  <button type="button" onClick={markAllRead} className="text-[9px] text-blue-light hover:underline">Mark all read</button>
                )}
                <button type="button" onClick={() => setOpen(false)} className="text-text-muted hover:text-text-primary" title="Close"><X size={12} /></button>
              </div>
            </div>
            <div className="max-h-[350px] overflow-y-auto">
              {notifications.length === 0 && (
                <p className="text-xs text-text-muted text-center py-6">No notifications</p>
              )}
              {notifications.map(n => {
                const Icon = SEV_ICON[n.severity] || Bell
                return (
                  <div key={n.id} className={clsx('px-3 py-2 border-b border-border hover:bg-surface-hover transition-colors',
                    !n.is_read && 'bg-blue/5')}>
                    <div className="flex items-start gap-2">
                      <Icon size={12} className={n.severity === 'critical' ? 'text-rose mt-0.5' : 'text-text-muted mt-0.5'} />
                      <div className="flex-1 min-w-0">
                        <p className={clsx('text-[10px] leading-snug', n.is_read ? 'text-text-secondary' : 'text-text-primary font-medium')}>{n.title}</p>
                        {n.body && <p className="text-[9px] text-text-muted mt-0.5 line-clamp-1">{n.body}</p>}
                        <span className="text-[8px] text-text-muted">{n.created_at?.slice(0, 16)}</span>
                      </div>
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-blue shrink-0 mt-1" />}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default function TopNav({ theme, onToggleTheme, onToggleSidebar }: TopNavProps) {
  const { logout, email } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim().length >= 2) {
      navigate(`/app/tech-news?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery('')
    }
  }

  return (
    <header className="h-12 border-b border-border flex items-center justify-between px-3 bg-surface">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onToggleSidebar} className="btn-ghost p-1 rounded-lg" aria-label="Toggle sidebar">
          <Menu size={16} />
        </button>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded bg-blue flex items-center justify-center">
            <span className="text-white font-bold text-[10px]">TD</span>
          </div>
          <span className="font-semibold text-text-primary text-xs hidden sm:block">TechDashboard</span>
        </div>
      </div>

      {/* World Clocks */}
      <WorldClocks />

      <div className="flex items-center gap-1.5">
        <form onSubmit={handleSearch} className="relative hidden md:block">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="input pl-7 py-1 w-44 text-[11px]"
          />
        </form>

        <TenantSwitcher />
        <MyViewSwitcher />
        <SavingsChip />
        <WhatsNewBadge />
        <NotificationBell />

        <button type="button" onClick={onToggleTheme} className="btn-ghost p-1 rounded-lg"
          aria-label={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
        </button>

        {email && <span className="text-[9px] text-text-muted hidden lg:block">{email}</span>}

        <button type="button" onClick={logout} className="btn-ghost p-1 rounded-lg text-text-muted hover:text-rose-light"
          aria-label="Sign out" title="Sign out">
          <LogOut size={14} />
        </button>
      </div>
    </header>
  )
}
